import { AlertStatus } from "@chakra-ui/react";
import { FormikHelpers } from "formik";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GET_FORM_ASSIGNMENTS_FOR_STUDENT_AND_TEMPLATE } from "src/components/graphql/queries";
import {
  useCreateFormForAdmin,
  useGetActiveFormsForApplicant,
  useGetEligibleFormTemplatesForApplicant,
} from "src/generated/hooks/form-service/queries";
import type {
  ApplicantSummary,
  EligibleFormTemplateSummary,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useStartForm } from "src/scenes/parent/forms/hooks/useStartForm";
import { Forms } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { useInvalidateFormList } from "./useFormsList";

/** Template from getEligibleFormTemplatesForStudent (includes can_start / cant_start_reason from API). */
type EligibleTemplateWithCanStart = EligibleFormTemplateSummary & {
  can_start: boolean;
  cant_start_reason?:
    | "already-started"
    | "not-in-audience"
    | "template-not-active"
    | "template-locked"
    | "incomplete-linked-form";
  // Present for `incomplete-linked-form`: the prerequisite (source) form to name
  // and link. `source_form_id` is null when the applicant has no source form.
  source_form_template_id?: string;
  source_form_template_name?: string;
  source_form_id?: string | null;
};

const CANT_START_MESSAGES: Record<
  Exclude<
    NonNullable<EligibleTemplateWithCanStart["cant_start_reason"]>,
    "incomplete-linked-form"
  >,
  string
> = {
  "already-started":
    "This student has already started a response for this form",
  "not-in-audience": "This student is not in the audience for this form",
  "template-not-active": "This form is not currently active",
  "template-locked": "This form is not accepting new submissions",
};

export const CreateFormFormSchema = z.object({
  organizationId: z.string().uuid("Organization is required"),
  enrollmentPeriodId: z.string().uuid("Enrollment period is required"),
  formTemplateId: z.string().uuid("Form is required"),
  applicantId: z.string().uuid("Student is required"),
});

export type CreateFormFormValues = z.infer<typeof CreateFormFormSchema>;

export type CreateFormSelectedApplicant = ApplicantSummary | null | undefined;

export type CreateFormSelectedTemplate =
  | EligibleFormTemplateSummary
  | null
  | undefined;

/** Minimal shape for create-form flows; dialogs may pass the full enrollment period from the API. */
export type CreateFormSelectedEnrollmentPeriod =
  | { id: string }
  | null
  | undefined;

interface UseCreateFormSubmitParams {
  selectedEnrollmentPeriod: CreateFormSelectedEnrollmentPeriod;
  selectedApplicant: CreateFormSelectedApplicant;
  selectedFormTemplate: CreateFormSelectedTemplate;
  onClose: (resetForm: () => void) => void;
}

export function useCreateFormSubmit({
  selectedEnrollmentPeriod,
  selectedApplicant,
  selectedFormTemplate,
  onClose,
}: UseCreateFormSubmitParams) {
  const enrollmentPeriodId = selectedEnrollmentPeriod?.id ?? "";
  const organization = useOrganization();
  const { startAssignedForm } = useStartForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const invalidateFormList = useInvalidateFormList();

  const {
    data: eligibleTemplatesResponse,
    isLoading: isEligibleTemplatesLoading,
  } = useGetEligibleFormTemplatesForApplicant(
    {
      query: {
        enrollmentPeriodId,
        applicantId: selectedApplicant?.id ?? "",
      },
    },
    undefined,
    {
      enabled: !!selectedEnrollmentPeriod?.id && !!selectedApplicant?.id,
      // Keep the prior result while refetching after an applicant change so the
      // can-start alert does not flash off and back on.
      placeholderData: (previousData) => previousData,
    }
  );
  const eligibleTemplates = React.useMemo(
    () =>
      (eligibleTemplatesResponse?.data ?? []) as EligibleTemplateWithCanStart[],
    [eligibleTemplatesResponse?.data]
  );

  const { remoteData: getAssignedFormsRD } = useRemoteDataQuery<
    GQL.GetFormAssignmentsForStudentAndTemplate,
    GQL.GetFormAssignmentsForStudentAndTemplateVariables
  >(GET_FORM_ASSIGNMENTS_FOR_STUDENT_AND_TEMPLATE, {
    variables: {
      applicant_id: selectedApplicant?.id ?? "",
      form_template_id: selectedFormTemplate?.id ?? "",
    },
    skip: !selectedApplicant || !selectedFormTemplate,
  });

  const { mutateAsync: createForm } = useCreateFormForAdmin();

  const { data: activeFormsResponse, refetch: refetchActiveForms } =
    useGetActiveFormsForApplicant(
      {
        query: {
          personId: selectedApplicant?.id ?? "",
        },
      },
      undefined,
      {
        enabled: !!selectedApplicant,
      }
    );

  const [activeFormsLookup, setActiveFormsLookup] = React.useState<
    Map<uuid, uuid>
  >(new Map());

  React.useEffect(() => {
    if (!activeFormsResponse?.data) {
      return;
    }
    const lookup = new Map<uuid, uuid>();
    activeFormsResponse.data.forEach(({ id, formTemplateId }) =>
      lookup.set(formTemplateId, id)
    );
    setActiveFormsLookup(lookup);
  }, [activeFormsResponse?.data]);

  const [alertText, setAlertText] = React.useState<React.ReactNode | null>(
    null
  );
  const [alertStatus, setAlertStatus] = React.useState<AlertStatus>("info");

  const templateCanStartInfo = React.useMemo(() => {
    if (!selectedFormTemplate) return null;
    const template = eligibleTemplates.find(
      (t) => t.id === selectedFormTemplate?.id
    ) as EligibleTemplateWithCanStart | undefined;
    if (!template) return null;
    return {
      can_start: template.can_start,
      cant_start_reason: template.cant_start_reason,
      source_form_template_id: template.source_form_template_id,
      source_form_template_name: template.source_form_template_name,
      source_form_id: template.source_form_id,
    };
  }, [eligibleTemplates, selectedFormTemplate]);

  React.useEffect(() => {
    // Without a selected applicant the eligible-templates data is stale (the query
    // is disabled and retains the previous applicant's result), so never derive an
    // alert from it. This also clears the alert when the student is cleared.
    if (!selectedFormTemplate || !selectedApplicant) {
      setAlertText(null);
      return;
    }
    if (eligibleTemplates.length > 0) {
      const template = eligibleTemplates.find(
        (t) => t.id === selectedFormTemplate?.id
      ) as EligibleTemplateWithCanStart | undefined;
      if (!template) {
        setAlertText("This student cannot start this form.");
        setAlertStatus("error");
        return;
      }
      if (template.can_start) {
        setAlertText(null);
        return;
      }
      const reason = template.cant_start_reason;
      if (reason === "incomplete-linked-form") {
        // PR-9: block and point to the source form. The dialog appends the
        // "See form." link when showSeeFormLink is set.
        const applicantLabel = selectedApplicant
          ? `${selectedApplicant.first_name} ${selectedApplicant.last_name}`
          : "This student";
        const sourceName =
          template.source_form_template_name ?? "the prior form";
        setAlertText(
          `${applicantLabel} can't start this form yet, no offers accepted on ${sourceName}.`
        );
      } else {
        const message = reason ? CANT_START_MESSAGES[reason] : null;
        setAlertText(message ?? "This student cannot start this form.");
      }
      setAlertStatus(reason === "already-started" ? "warning" : "error");
    } else {
      setAlertText(null);
    }
  }, [selectedFormTemplate, selectedApplicant, eligibleTemplates]);

  const showSeeFormLink =
    templateCanStartInfo?.cant_start_reason === "already-started" ||
    (templateCanStartInfo?.cant_start_reason === "incomplete-linked-form" &&
      !!templateCanStartInfo?.source_form_template_id &&
      !!templateCanStartInfo?.source_form_id);

  const canStartForm =
    selectedApplicant != null &&
    !isEligibleTemplatesLoading &&
    templateCanStartInfo != null &&
    templateCanStartInfo.can_start;

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmitForm = React.useCallback(
    async (
      values: CreateFormFormValues,
      { resetForm }: FormikHelpers<CreateFormFormValues>
    ) => {
      if (!canStartForm) {
        toast({
          title: "Cannot create form",
          description: "This form cannot be created for this student",
          status: "error",
          isClosable: true,
        });
        return;
      }
      try {
        setIsSubmitting(true);
        if (getAssignedFormsRD.hasError()) {
          console.error(
            `Could not create form: ${JSON.stringify(getAssignedFormsRD.error)}`
          );
          toast({
            title: "Failed to create form",
            status: "error",
            isClosable: true,
          });
          return;
        }

        let assignedForm = undefined;
        if (getAssignedFormsRD.hasData()) {
          assignedForm = getAssignedFormsRD.data.assigned_form.find(
            (x) => x.form_template.id === values.formTemplateId
          );
        }

        let newFormId: uuid | undefined = undefined;
        if (assignedForm) {
          newFormId = await startAssignedForm(assignedForm);
          await refetchActiveForms();
        } else {
          newFormId = (
            await createForm({
              body: {
                data: {
                  personId: values.applicantId,
                  formTemplateId: values.formTemplateId,
                },
              },
            })
          ).data?.data.id;
          await refetchActiveForms();
        }
        invalidateFormList();

        onClose(resetForm);

        toast({
          title: "Form created",
          status: "success",
          isClosable: true,
        });

        // This is a workaround to fix the issue where the modal is not closed before the form is opened, which will
        //disable scrolling on the form detail page.
        setTimeout(() => {
          navigate(
            organization
              .map(() =>
                Forms.view({
                  organization,
                  id: newFormId || "",
                  formTemplateId: values.formTemplateId,
                  params: searchParams.toString(),
                })
              )
              .withDefault("#")
          );
        }, 100);
      } catch (error) {
        console.error(error);
        toast({
          title: "Could not create form",
          status: "error",
          isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      canStartForm,
      onClose,
      createForm,
      navigate,
      organization,
      searchParams,
      toast,
      startAssignedForm,
      getAssignedFormsRD,
      refetchActiveForms,
      invalidateFormList,
    ]
  );

  const handleOnSeeFormClick = React.useCallback(
    (handleClose: () => void) => {
      // For a follow-on prerequisite the link points at the source form (resolved
      // server-side, possibly outside the staffer's own visibility); otherwise it
      // points at the applicant's form on the selected template.
      let target: { formTemplateId: uuid; id: string } | null = null;
      if (
        templateCanStartInfo?.cant_start_reason === "incomplete-linked-form" &&
        templateCanStartInfo.source_form_template_id &&
        templateCanStartInfo.source_form_id
      ) {
        target = {
          formTemplateId: templateCanStartInfo.source_form_template_id,
          id: templateCanStartInfo.source_form_id,
        };
      } else if (selectedFormTemplate?.id) {
        const id = activeFormsLookup?.get(selectedFormTemplate.id);
        if (id) {
          target = { formTemplateId: selectedFormTemplate.id, id };
        }
      }
      if (!target) return;
      const resolved = target;
      handleClose();
      setTimeout(() => {
        navigate(
          Forms.view({
            organization,
            id: resolved.id,
            formTemplateId: resolved.formTemplateId,
            params: searchParams.toString(),
          })
        );
      }, 100);
    },
    [
      navigate,
      organization,
      searchParams,
      activeFormsLookup,
      templateCanStartInfo,
      selectedFormTemplate,
    ]
  );

  return {
    handleSubmitForm,
    getAssignedFormsRD,
    isEligibleTemplatesLoading,
    alertText,
    alertStatus,
    showSeeFormLink: showSeeFormLink ?? false,
    handleOnSeeFormClick,
    isSubmitting,
    canStartForm,
    cantStartReason: templateCanStartInfo?.cant_start_reason,
    activeFormsLookup,
    resetAlert: React.useCallback(() => {
      setAlertText(null);
      setAlertStatus("info");
    }, []),
  };
}
